import { useState, useEffect, useRef } from "react";
import type { CardDisplayMode } from "@/components/dashboard/CardDisplayToggle";

interface UseScrollResponsiveCardsProps {
  initialMode: CardDisplayMode;
}

export const useScrollResponsiveCards = ({ initialMode }: UseScrollResponsiveCardsProps) => {
  const [currentMode, setCurrentMode] = useState<CardDisplayMode>(initialMode);
  const [isSticky, setIsSticky] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const lastScrollY = useRef(0);
  const scrollThresholds = {
    compact: 150,  // Switch to compact after 150px scroll
    hidden: 400,   // Hide cards after 400px scroll
  };

  useEffect(() => {
    if (initialMode !== "full") {
      setCurrentMode(initialMode);
      setIsSticky(false);
      return;
    }

    // Get the main scrolling container
    const scrollContainer = document.querySelector('main');
    if (!scrollContainer) return;

    const handleScroll = () => {
      const currentScrollY = scrollContainer.scrollTop;
      const scrollDirection = currentScrollY > lastScrollY.current ? 'down' : 'up';
      lastScrollY.current = currentScrollY;

      // Determine stickiness based on element position relative to the scroll container
      const el = searchBarRef.current;
      if (el) {
        const containerTop = (scrollContainer as HTMLElement).getBoundingClientRect().top;
        const elTop = el.getBoundingClientRect().top;
        setIsSticky(elTop <= containerTop + 1);
      }

      // Card mode changes
      if (scrollDirection === 'down') {
        if (currentScrollY > scrollThresholds.hidden) {
          setCurrentMode("hidden");
        } else if (currentScrollY > scrollThresholds.compact) {
          setCurrentMode("compact");
        }
      } else {
        // Scrolling up
        if (currentScrollY < scrollThresholds.compact) {
          setCurrentMode("full");
        } else if (currentScrollY < scrollThresholds.hidden && currentMode === "hidden") {
          setCurrentMode("compact");
        }
      }
    };

    console.log('Setting up scroll listener');
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    requestAnimationFrame(handleScroll);
    return () => {
      console.log('Removing scroll listener');
      scrollContainer.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [initialMode, currentMode]);

  return {
    currentMode,
    isSticky,
    scrollContainerRef,
    searchBarRef,
  };
};